import { type RendererResult } from '@ekkogmbh/apisdk';
import { isBase64 } from 'src/Common/formatSupport';

/**
 * What the preview has to show for a given render result.
 */
export interface PreviewDisplay {
  /** The rendered image, when the renderer produced one. */
  image?: string;
  /** Renderer text that arrived in place of an image. */
  text?: string;
  /** One entry per diagnostic the renderer reported. */
  messages: string[];
  /** Whether to hold the placeholder rather than report anything. */
  isPending: boolean;
  /** Whether the template is known to have been refused. */
  isFailed: boolean;
}

/**
 * A template the renderer refuses is answered with `image` set to `null` and one
 * `errors` entry per problem found, so the absence of an image is the ordinary
 * shape of a failure rather than a sign that nothing has arrived yet. The two
 * are told apart by whether any diagnostic came with it.
 */
export const derivePreviewDisplay = (rendering?: RendererResult): PreviewDisplay => {
  const messages = rendering?.errors ?? [];
  // An empty image is no image either, whatever the renderer meant by it.
  const image = rendering?.image !== null && rendering?.image !== '' ? rendering?.image : undefined;

  if (image === undefined) {
    return { messages, isPending: messages.length === 0, isFailed: messages.length > 0 };
  }

  if (isBase64(image)) {
    return { image, messages, isPending: false, isFailed: false };
  }

  // API versions before the renderer's diagnostics were read out of the response
  // answered a refused template with the renderer's error text in this field.
  return { text: image, messages, isPending: false, isFailed: true };
};
